import { useCallback } from 'react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

export const UpdateCalendarPostDueAt = graphql(/* GraphQL */ `
  mutation UpdateCalendarPostDueAt($input: DropPostInput!) {
    dropPost(input: $input) {
      __typename
      ... on PostActionSuccess {
        post {
          __typename
          id
          dueAt
        }
      }
      ... on MutationError {
        message
      }
    }
  }
`)

export type UpdateCalendarPostDueAtData = ResultOf<
  typeof UpdateCalendarPostDueAt
>

export type SuccessData = {
  id: string
  dueAt?: string | null | undefined
}

export const getSuccessFragmentData = (
  data: UpdateCalendarPostDueAtData | null | undefined,
): SuccessData | undefined | null => {
  const response = data?.dropPost
  const typename = response?.__typename
  if (typename !== 'PostActionSuccess') return null
  return response?.post
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export const useUpdatePostDueAt = () => {
  const [mutate, { loading, error, success, data }] = useTypedMutation(
    UpdateCalendarPostDueAt,
    (mutationData) => mutationData.dropPost,
    {
      successTypename: 'PostActionSuccess',
    },
  )

  const updatePostDueAt = useCallback(
    async ({
      id,
      dueAt,
      isPinned,
    }: {
      id: string
      dueAt: string
      isPinned: boolean
    }) => {
      const result = await mutate({
        variables: {
          input: {
            id,
            dueAt,
            isPinned,
          },
        },
        optimisticResponse: {
          dropPost: {
            __typename: 'PostActionSuccess',
            post: {
              __typename: 'Post',
              id,
              dueAt,
            },
          },
        },
      })

      if (!result.success) {
        throw new Error(result.error.message)
      }

      return result.data.post
    },
    [mutate],
  )

  return {
    updatePostDueAt,
    loading,
    error,
    data: success ? data : null,
  }
}
